import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Zap, Landmark, X, Clock } from "lucide-react";

const fmt = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TONE = { good: "border-good bg-good/10", warn: "border-warn bg-warn/10", bad: "border-bad bg-bad/10" };

// Clickable funding status bar → Cash Flow Drawer (last 3 funding events).
export const FundingHealthBar = () => {
  const [health, setHealth] = useState(null);
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [fees, setFees] = useState(null);

  const load = useCallback(async () => {
    try { setHealth((await api.get("/funding/health")).data); } catch { /* optional */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDrawer = async () => {
    setOpen(true);
    setLoadingLedger(true);
    try {
      const [led, fees] = await Promise.all([
        api.get("/funding/ledger?limit=3"),
        api.get("/funding/instant-fees?months=6"),
      ]);
      setEvents(led.data.events || []);
      setFees(fees.data);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoadingLedger(false);
  };

  if (!health) return null;
  return (
    <>
      <button
        onClick={openDrawer}
        data-testid="funding-health-bar"
        className={`w-full text-left border-2 ${TONE[health.tone] || "border-ink"} p-4 mb-8 flex items-center justify-between gap-4 hover:shadow-hard transition-shadow`}
      >
        <div className="flex items-center gap-3">
          {health.funding_mode === "instant_card" ? <Zap className="w-5 h-5 text-good" /> : <Landmark className="w-5 h-5" />}
          <div>
            <div className="font-heading font-black text-lg" data-testid="funding-health-headline">{health.headline}</div>
            <div className="font-mono text-[11px] text-muted-foreground">{health.detail}</div>
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest border-2 border-ink px-2 py-1">Cash Flow →</span>
      </button>

      {health.balance?.known && (
        <div className="-mt-6 mb-8 font-mono text-[10px] text-muted-foreground flex flex-wrap gap-x-4" data-testid="funding-balance-line">
          <span>Live Stripe balance ({health.balance.ledger}): <b className="text-ink">{fmt(health.balance.available_usd)}</b> available</span>
          <span>{fmt(health.balance.pending_usd)} still clearing</span>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="cash-flow-drawer">
          <button aria-label="Close" onClick={() => setOpen(false)} className="flex-1 bg-ink/50" />
          <div className="w-full max-w-md bg-white border-l-2 border-ink h-full p-6 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="overline mb-1">§ CASH FLOW</div>
                <h2 className="font-heading font-black text-3xl">Funding Ledger</h2>
              </div>
              <button onClick={() => setOpen(false)} data-testid="cash-flow-close" className="border-2 border-ink p-1.5"><X className="w-4 h-4" /></button>
            </div>
            <div className="font-mono text-[11px] text-muted-foreground mb-4">{health.headline} · {health.detail}</div>
            {health.balance?.known && (
              <div className="border-2 border-ink p-3 mb-4 font-mono text-[11px]" data-testid="drawer-balance">
                <div className="flex justify-between"><span>Available now ({health.balance.ledger})</span><b>{fmt(health.balance.available_usd)}</b></div>
                <div className="flex justify-between"><span>Still clearing</span><b className="text-warn">{fmt(health.balance.pending_usd)}</b></div>
              </div>
            )}
            {loadingLedger ? (
              <div className="space-y-3" data-testid="ledger-loading">
                {[0, 1, 2].map((i) => <div key={i} className="h-12 border-2 border-ink/20 bg-ink/5 animate-pulse" />)}
              </div>
            ) : events.length === 0 ? (
              <div className="font-mono text-xs text-muted-foreground" data-testid="ledger-empty">No funding events yet.</div>
            ) : (
              <ul className="divide-y-2 divide-ink/20" data-testid="ledger-list">
                {events.map((ev) => (
                  <li key={ev.id} className="py-3" data-testid={`ledger-event-${ev.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-mono text-xs font-black">{ev.label}</div>
                      {ev.amount != null && <div className="font-mono text-sm font-black shrink-0">{fmt(ev.amount)}</div>}
                    </div>
                    <div className="mt-1 flex items-center gap-2 font-mono text-[10px] text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{new Date(ev.occurred_at).toLocaleString()}</span>
                      <span className={`border px-1 uppercase tracking-widest ${ev.available_state === "pending" ? "border-warn text-warn" : "border-good text-good"}`}>
                        {ev.available_state === "pending" ? "clears" : "available"} {new Date(ev.available_at).toLocaleString()}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {fees && (
              <div className="mt-6 border-t-2 border-ink pt-4" data-testid="instant-fee-report">
                <div className="overline mb-1">§ INSTANT DELIVERY COST</div>
                <div className="font-mono text-[11px] text-muted-foreground mb-3">{fees.fee_rate} · absorbed by the business</div>
                {fees.months.length === 0 ? (
                  <div className="font-mono text-xs text-muted-foreground" data-testid="instant-fee-empty">No instant payouts yet — no fees charged.</div>
                ) : (
                  <>
                    <div className="flex justify-between font-mono text-xs font-black mb-2" data-testid="instant-fee-total">
                      <span>Total fees (last {fees.months.length} month{fees.months.length === 1 ? "" : "s"})</span>
                      <span className="text-bad">{fmt(fees.total_fee_usd)}</span>
                    </div>
                    <ul className="divide-y divide-ink/20">
                      {fees.months.map((m) => (
                        <li key={m.month} className="py-2" data-testid={`instant-fee-month-${m.month}`}>
                          <div className="flex justify-between font-mono text-xs">
                            <b>{m.month}</b>
                            <span className="text-bad font-black">{fmt(m.fee_usd)}</span>
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {m.payout_count} instant payout{m.payout_count === 1 ? "" : "s"} · {fmt(m.paid_usd)} delivered
                          </div>
                          <ul className="mt-1 space-y-0.5">
                            {m.runs.map((r) => (
                              <li key={r.run_id} className="flex justify-between font-mono text-[10px] text-muted-foreground">
                                <span>Run {r.pay_date}</span><span>{fmt(r.fee_usd)}</span>
                              </li>
                            ))}
                          </ul>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
